"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { useSession } from "@/hooks/useSession";
import { User } from "@/types";
import { FeatureFlag, FeatureFlagName } from "@/types/feature-flag";
import { Member } from "stytch";
import { LoadingSpinner } from "@focal/ui-kit";
import { OAuthOnboardingHandler } from "@/components/layout/OAuthOnboardingHandler";

export interface SessionContextType {
  session: Member | null | undefined;
  userName: string | undefined;
  isReady: boolean;
  userId?: string;
  user?: User;
  handleLogout: () => void;
  getFeatureFlag: (featureName: FeatureFlagName) => FeatureFlag;
  isFeatureFlagFetched?: boolean;
  userRoles: string[];
  region: string;
}

// Exported (only) so component tests can supply a fake value via
// `SessionContext.Provider`, bypassing `SessionProvider`'s real session chain.
export const SessionContext = createContext<SessionContextType | null>(null);

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const sessionData = useSession();

  // Feature flags determine what gets rendered, so block children until they're
  // loaded for a signed-in user. The `!userId` escape lets logged-out users on a
  // protected route fall through (the flags query is disabled, so it never
  // "fetches") to the auth redirect instead of spinning forever.
  const featureFlagsReady =
    !sessionData.userId || sessionData.isFeatureFlagFetched;
  const isReady = sessionData?.isReady && featureFlagsReady;

  return (
    <SessionContext.Provider value={sessionData}>
      {isReady ? (
        <OAuthOnboardingHandler>{children}</OAuthOnboardingHandler>
      ) : (
        <div className="flex flex-1 h-screen items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </SessionContext.Provider>
  );
};

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error("useSessionContext must be used within a SessionProvider");
  }
  return context;
};
